import Swal from "sweetalert2";

// Helper to safely extract a meaningful message from various error payload shapes
function extractMessage(payload) {
    try {
        if (payload == null) return undefined;
        if (typeof payload === 'string') return payload;
        if (payload instanceof Error) return payload.message || String(payload);
        if (typeof payload === 'object') {
            // Common server shapes
            const m = payload.message || payload.Message || payload.error || payload.Error || payload.response_message || payload.responseMessage;
            if (typeof m === 'string') return m;
            // Some APIs embed printable messages
            const p = payload.Print;
            if (p != null) {
                const s = p.toString();
                if (typeof s === 'string' && s.toLowerCase().includes('failed')) return s;
                return s;
            }
            // Axios-like nested response bodies
            if (payload.response && typeof payload.response === 'object') {
                const nested = extractMessage(payload.response.data ?? payload.response);
                if (nested) return nested;
            }
            // Fallback to stringifying object
            return JSON.stringify(payload);
        }
        return String(payload);
    } catch (_) {
        // Never throw from extraction
        return undefined;
    }
}

// Notify user via responses.throwErr if available, else fallback to Swal when allowed
function notifyUser(message, title, allowSweetAlert) {
    const safeMessage = message ?? 'An unexpected error occurred.';
    const safeTitle = title ?? 'Error';

    const hasResponses = typeof responses !== 'undefined' && responses && typeof responses.throwErr === 'function';

    if (hasResponses) {
        try {
            responses.throwErr(safeMessage, safeTitle);
            return;
        } catch (e) {
            // Fall through to SweetAlert if responses fails for any reason
        }
    }

    if (allowSweetAlert) return;

    try {
        Swal.fire({
            title: safeTitle,
            icon: 'error',
            text: safeMessage,
            showCloseButton: true,
        });
    } catch (_) {
        // Swallow to avoid secondary failures in error paths
        // As last resort, log
        console.error('Notification failed:', safeTitle, safeMessage);
    }
}

export const errorHandler = {
    throwNotFound() {
        return 'Failed to Connect to Server (Url Not Found)!';
    },

    throwTimeOut() {
        return 'Connection Time Out!';
    },

    throwNoData() {
        return 'There are no team members for the selected team!';
    },

    throwNoRoleData() {
        return "You don't have any roles assigned to you, please contact your Administrator!";
    },

    franchiseLoginError(err) {
        const response = err?.response;
        let errorMessage;

        if (response) {
            console.error(response);
            if (response.status === 404) {
                errorMessage = this.throwNotFound();
            } else {
                errorMessage = extractMessage(response.data);
            }
        } else if (err?.code === 'ECONNABORTED') {
            errorMessage = this.throwTimeOut();
        } else {
            console.error(err);
            errorMessage = extractMessage(err) ?? err?.message;
        }

        if (errorMessage === 'Network Error') {
            errorMessage = 'Please Check your Internet';
        }

        return errorMessage ?? 'An unexpected error occurred.';
    },

    tomcatError(err, isSweetalert = false, title = undefined) {
        // Pass through plain strings and primitives (covers text/plain server responses and manual rejects)
        if (typeof err === 'string') {
            const msg = err.trim();
            return msg.length ? msg : 'An error occurred';
        }
        if (err !== null && typeof err !== 'object') {
            return String(err);
        }

        const response = err?.response;
        let errorMessage;

        if (response) {
            console.error(response);
            if (response.status === 404) {
                errorMessage = this.throwNotFound();
            } else if (response.status === 408) {
                errorMessage = this.throwNoRoleData();
            } else if (response.status === 500) {
                // Normalize server error to a string and avoid exposing stack traces to end users
                const raw = String(extractMessage(response.data) ?? '');
                const shortDetail = raw.split('\n').find(l => l && l.trim().length > 0) || '';
                console.error('[500] Server Error:', raw);
                // Keep original behavior: show SweetAlert and return the promise (no throw here)
                return Swal.fire({
                    title: 'An Error Occurred',
                    icon: 'error',
                    text: 'Something went wrong on the server. Please try again. If the problem persists, contact support.',
                    footer: shortDetail ? `Details: ${shortDetail.substring(0, 200)}${raw.length > 200 ? '…' : ''}` : undefined,
                    showCloseButton: true,
                });
            } else if (response.status === 503) {
                const rawMsg = extractMessage(response.data);
                if (typeof rawMsg === 'string' && rawMsg.includes('Expected BEGIN_OBJECT but was STRING')) {
                    errorMessage = 'ZRA registration failed — the device TPIN or serial number may not be registered with ZRA. Please verify the store details.';
                } else {
                    errorMessage = rawMsg;
                }
            } else {
                errorMessage = extractMessage(response.data);
            }
        } else if (err?.code === 'ECONNABORTED') {
            errorMessage = this.throwTimeOut();
        } else {
            console.error(err);
            errorMessage = extractMessage(err) ?? err?.message;
        }

        if (errorMessage === 'Network Error') {
            title = title || errorMessage;
            errorMessage = 'Please Check your Internet';
        }

        // Final hardening
        errorMessage = errorMessage ?? 'An unexpected error occurred.';

        if (!isSweetalert) {
            notifyUser(errorMessage, title, /*allowSweetAlert*/ false);
        }

        throw new Error(errorMessage);
    },

    customerKazangError(err, isSweetalert = false) {
        console.log(err);
        const response = err?.response;
        let errorMessage;

        if (response) {
            console.error(response);
            if (response.status === 404) {
                errorMessage = this.throwNotFound();
            } else {
                errorMessage = extractMessage(response.data);
            }
        } else if (err?.Message || err?.Error || err?.message) {
            // Prefer lowercase .message if present
            errorMessage = err?.message || err?.Message || err?.Error;
        } else if (err?.code === 'ECONNABORTED') {
            errorMessage = this.throwTimeOut();
        }

        errorMessage = errorMessage ?? 'An unexpected error occurred.';

        if (!isSweetalert) {
            notifyUser(errorMessage, undefined, /*allowSweetAlert*/ false);
        }

        throw new Error(errorMessage);
    },

    /**
     * Normalize any error into a user-safe object without side effects.
     * Mirrors translation from tomcatError but returns an object instead of throwing/showing UI.
     * @param {any} err
     * @returns {{ message: string, status?: number, code?: string, raw: any }}
     */
    normalizeError(err) {
        const response = err?.response;
        let message;
        let status;
        const code = err?.code;

        if (response) {
            status = response.status;
            if (status === 404) {
                message = this.throwNotFound();
            } else if (status === 408) {
                message = this.throwNoRoleData();
            } else if (status === 500) {
                const raw = String(extractMessage(response.data) ?? '');
                const shortDetail = raw.split('\n').find(l => l && l.trim().length > 0) || '';
                message = shortDetail || 'Server error, please try again.';
            } else if (status === 503) {
                const raw = String(extractMessage(response.data) ?? '');
                if (raw.includes('Expected BEGIN_OBJECT but was STRING')) {
                    message = 'ZRA registration failed — the device TPIN or serial number may not be registered with ZRA.';
                } else {
                    message = raw;
                }
            } else {
                message = extractMessage(response.data);
            }
        } else if (code === 'ECONNABORTED') {
            message = this.throwTimeOut();
        } else {
            message = extractMessage(err) ?? err?.message;
        }

        if (message === 'Network Error') {
            message = 'Please Check your Internet';
        }

        return {
            message: message || 'An unexpected error occurred.',
            status,
            code,
            raw: err,
        };
    },

    multi: {
        /**
         * Aggregate multiple async tasks sequentially and collect outcomes.
         * @template T
         * @param {T[]} items
         * @param {(item: T) => Promise<any>} mapper
         * @returns {Promise<{ successCount: number, failCount: number, failures: Array<{ item: T, error: { message: string, status?: number, code?: string, raw: any } }> }>} 
         */
        async aggregate(items, mapper) {
            const out = { successCount: 0, failCount: 0, failures: [] };
            for (const item of items) {
                try {
                    await mapper(item);
                    out.successCount += 1;
                } catch (e) {
                    const normalized = errorHandler.normalizeError(e);
                    out.failCount += 1;
                    out.failures.push({ item, error: normalized });
                }
            }
            return out;
        },

        /**
         * Show a standard summary with optional failure details and optional retry action.
         * @param {{ successCount:number, failCount:number, failures:Array<{ item:any, error:{ message:string } }> }} summary
         * @param {{ title?: string, allowRetry?: boolean, formatItem?: (f:any)=>string }} uiOpts
         * @returns {Promise<{ acknowledged: boolean, retry: boolean }>}
         */
        async showSummary(summary, uiOpts = {}) {
            const title = uiOpts.title || 'Operation summary';
            const { successCount = 0, failCount = 0, failures = [] } = summary || {};

            const summaryHtml = `
      <div class="text-start">
        <div><strong>Success:</strong> ${successCount}</div>
        <div><strong>Failed:</strong> ${failCount}</div>
      </div>
    `;

            const showFailedButton = failCount > 0;

            const res = await Swal.fire({
                icon: showFailedButton ? 'warning' : 'success',
                title,
                html: summaryHtml,
                confirmButtonText: 'OK',
                showDenyButton: showFailedButton,
                denyButtonText: `View failed (${failCount})`,
            });

            if (res.isDenied && showFailedButton) {
                const formatItem = uiOpts.formatItem || ((f) => {
                    try {
                        const name = f?.item?.storeName || f?.item?.name || f?.item?.id || '[item]';
                        const extra = f?.item?.store_database ? `DB: ${f.item.store_database}` : '';
                        return `${name}${extra ? ` — ${extra}` : ''}`;
                    } catch { return '[item]'; }
                });

                const failedListHtml = failures.map((f, idx) => (
                    `
         <div class="mb-2 p-2 border rounded">
           <div class="fw-bold">${idx + 1}. ${formatItem(f)}</div>
           <div class="text-danger mt-1">${f?.error?.message || 'Unexpected error'}</div>
         </div>
        `
                )).join('');

                const detailHtml = `<div style="max-height: 50vh; overflow:auto; text-align:left;">${failedListHtml}</div>`;

                const r = await Swal.fire({
                    icon: 'error',
                    title: `Failed details (${failCount})`,
                    html: detailHtml,
                    showCancelButton: true,
                    cancelButtonText: 'Close',
                    showConfirmButton: !!uiOpts.allowRetry,
                    confirmButtonText: 'Retry failed',
                });

                return { acknowledged: true, retry: !!r.isConfirmed };
            }

            return { acknowledged: true, retry: false };
        }
    }
};